// The content that names BOTH case studies of a two-case workshop at once:
// the introduction's context line, the topic sentence options per theme, the
// three positions with their reasons and counter-arguments, the position hint
// and the merged checks. Leah's model (14 Oct 2026): each case study's
// edition supplies five short lines per theme (what changed, what limited or
// reversed it, a reason, a counter, what you will need to show) and a context
// line; the pair's sentences are BUILT from them by the templates in copy.ts,
// and the topic sentence options follow the learner's own judgements on the
// compare screen. A hand-written `pairs` entry replaces the generated value
// for that pair, field by field. `renderPairReview` prints every sentence for
// every reachable pair, marked hand-written or generated, for Leah to check
// before release.
//
// Pure. The MCP tool and the builder's edit view call renderPairReview; the
// argue and plan steps will call pairContent and topicSentenceOptions.

import type { WorkshopBlock, WorkshopChecks, WorkshopEdition, WorkshopHints, WorkshopPosition, WorkshopStance, WorkshopTopicSentence } from '../types';
import { workshopPairKey } from '../types';
import { capitalise, copyFor, copyVars, fillCopy, reachable } from './steps';

export type PairSource = 'hand' | 'generated';

export interface PairContent {
  key: string;
  cases: [string, string];
  context: string;
  topicSentences: WorkshopTopicSentence[][];
  positions: WorkshopPosition[];
  hints: WorkshopHints;
  checks: WorkshopChecks;
  source: Record<'context' | 'topicSentences' | 'positions' | 'hints' | 'checks', PairSource>;
}

const DEFAULT_STANCES: WorkshopStance[] = ['for', 'against', 'mixed'];

/** Every theme of both editions carries its compare lines. */
export function canGenerate(w: WorkshopBlock, a: string, b: string): boolean {
  return [a, b].every((k) => {
    const e = w.editions[k];
    return !!e && e.themes.length > 0 && e.themes.every((t) => !!t.compare);
  });
}

/** The placeholder values for one theme of a pair: both case studies' names and lines. */
function themeVars(w: WorkshopBlock, a: string, b: string, i: number): Record<string, string> {
  const ea = w.editions[a];
  const eb = w.editions[b];
  const ca = ea.themes[i]?.compare;
  const cb = eb.themes[i]?.compare;
  return {
    ...copyVars(w, [a, b]),
    // Both names mid-sentence ("the First World War"); `sentence` capitalises
    // whichever word opens the finished sentence.
    A: w.caseStudies[a]?.name ?? a,
    B: w.caseStudies[b]?.name ?? b,
    changedA: ca?.changed ?? '',
    changedB: cb?.changed ?? '',
    limitedA: ca?.limited ?? '',
    limitedB: cb?.limited ?? '',
    reasonA: ca?.reason ?? '',
    reasonB: cb?.reason ?? '',
    counterA: ca?.counter ?? '',
    counterB: cb?.counter ?? '',
    showA: ca?.show ?? '',
    showB: cb?.show ?? '',
    contextA: ea.context,
    contextB: eb.context,
  };
}

/** A generated sentence: the template filled, then tidied so it opens with a capital. */
function sentence(e: WorkshopEdition, key: Parameters<typeof copyFor>[1], vars: Record<string, string>): string {
  return capitalise(copyFor(e, key, vars).trim());
}

/**
 * The three topic sentence options for one theme of a pair, arguing
 * different things: both changed, both limited, one changed while the other
 * was limited. With the learner's judgements (the compare scale's index for
 * each case study, 0 the strongest change) the option closest to their
 * judgement comes first, and a contrast runs from the case they judged
 * changed to the one they judged limited.
 */
export function topicSentenceOptions(w: WorkshopBlock, [a, b]: [string, string], theme: number, judgement?: { a: number; b: number }): WorkshopTopicSentence[] {
  const e = w.editions[a];
  const need = (x: string, y: string) => sentence(e, 'tsNeed', { ...themeVars(w, a, b, theme), showA: w.editions[x].themes[theme].compare?.show ?? '', showB: w.editions[y].themes[theme].compare?.show ?? '' });
  const similar: WorkshopTopicSentence = { text: sentence(e, 'tsSimilar', themeVars(w, a, b, theme)), need: need(a, b) };
  const limited: WorkshopTopicSentence = { text: sentence(e, 'tsLimited', themeVars(w, a, b, theme)), need: need(a, b) };
  const contrastAB: WorkshopTopicSentence = { text: sentence(e, 'tsContrast', themeVars(w, a, b, theme)), need: need(a, b) };
  const contrastBA: WorkshopTopicSentence = { text: sentence(e, 'tsContrast', themeVars(w, b, a, theme)), need: need(b, a) };
  if (!judgement) return [similar, limited, contrastAB];
  if (judgement.a === judgement.b) return judgement.a === 0 ? [similar, limited, contrastAB] : [limited, similar, contrastAB];
  // A lower index is the stronger change: the contrast runs from the stronger to the weaker.
  const contrast = judgement.a < judgement.b ? contrastAB : contrastBA;
  return [contrast, similar, limited];
}

function stanceOf(w: WorkshopBlock, i: number): WorkshopStance {
  return w.readings[i]?.stance ?? DEFAULT_STANCES[i] ?? 'mixed';
}

/** The three readings as positions, each with a reason and a counter per theme built from both case studies' lines. */
export function generatePositions(w: WorkshopBlock, [a, b]: [string, string]): WorkshopPosition[] {
  const e = w.editions[a];
  const n = e.themes.length;
  return w.readings.map((r, i) => {
    const stance = stanceOf(w, i);
    const line = (theme: number, side: 'reason' | 'counter') => {
      const vars = themeVars(w, a, b, theme);
      const key = stance === 'for' ? (side === 'reason' ? 'pairReason' : 'pairCounter') : stance === 'against' ? (side === 'reason' ? 'pairCounter' : 'pairReason') : side === 'reason' ? 'pairMixedReason' : 'pairMixedCounter';
      return copyFor(e, key, vars).trim();
    };
    return {
      title: r.title,
      detail: r.text,
      build: sentence(e, 'posBuild', { ...themeVars(w, a, b, 0), title: r.title }),
      reasons: Array.from({ length: n }, (_, t) => ({ theme: t, text: line(t, 'reason') })),
      counters: Array.from({ length: n }, (_, t) => ({ theme: t, text: line(t, 'counter') })),
    };
  });
}

/**
 * A generated position hint: when the learner judged most cells the weakest
 * change, the reading against the claim; most cells the strongest, the
 * reading for it; otherwise the mixed reading (or the first).
 */
export function generateHints(w: WorkshopBlock): WorkshopHints {
  const n = Math.max(1, Object.values(w.editions)[0]?.themes.length ?? 1);
  const scale = w.scale?.labels.length ?? 3;
  const idx = (st: WorkshopStance) => w.readings.findIndex((r, i) => stanceOf(w, i) === st);
  const against = idx('against');
  const forIdx = idx('for');
  const mixed = idx('mixed');
  const order: WorkshopHints['order'] = [];
  if (against >= 0) order.push({ position: against, when: [{ judgement: scale - 1, min: n }] });
  if (forIdx >= 0) order.push({ position: forIdx, when: [{ judgement: 0, min: n }] });
  return { order, fallback: mixed >= 0 ? mixed : Math.max(0, forIdx) };
}

/** The two editions' checks as one: the fragment lists joined, the year range widened, both case messages kept. */
export function mergeChecks(w: WorkshopBlock, [a, b]: [string, string]): WorkshopChecks {
  const ca = w.editions[a].checks;
  const cb = w.editions[b].checks;
  const union = (x: string[] = [], y: string[] = []) => [...new Set([...x, ...y])];
  const out: WorkshopChecks = { claim: union(ca.claim, cb.claim), link: union(ca.link, cb.link), themeWords: union(ca.themeWords, cb.themeWords) };
  const hits = [ca.outlineHits, cb.outlineHits].filter((x): x is number => typeof x === 'number');
  if (hits.length) out.outlineHits = Math.max(...hits);
  if (ca.yearRange || cb.yearRange) {
    const from = Math.min(ca.yearRange?.[0] ?? Infinity, cb.yearRange?.[0] ?? Infinity);
    const to = Math.max(ca.yearRange?.[1] ?? -Infinity, cb.yearRange?.[1] ?? -Infinity);
    out.yearRange = [from, to];
  }
  const msg = [ca.caseMessage, cb.caseMessage].filter(Boolean).join(' ');
  if (msg) out.caseMessage = msg;
  if (ca.extra?.length || cb.extra?.length) out.extra = [...(ca.extra ?? []), ...(cb.extra ?? [])];
  return out;
}

/** The pair's content: a hand-written field wins; generated fills the rest; null when a field has neither. */
export function pairContent(w: WorkshopBlock, cases: [string, string]): PairContent | null {
  const [a, b] = cases;
  if (!w.editions[a] || !w.editions[b]) return null;
  const key = workshopPairKey(a, b);
  const hand = w.pairs?.[key];
  const gen = canGenerate(w, a, b);
  const e = w.editions[a];
  const n = e.themes.length;
  const context = hand?.context ?? (gen ? sentence(e, 'pairContext', themeVars(w, a, b, 0)) : null);
  const topicSentences = hand?.topicSentences ?? (gen ? Array.from({ length: n }, (_, i) => topicSentenceOptions(w, cases, i)) : null);
  const positions = hand?.positions ?? (gen ? generatePositions(w, cases) : null);
  const hints = hand?.hints ?? (gen ? generateHints(w) : null);
  if (context === null || !topicSentences || !positions || !hints) return null;
  const merged = mergeChecks(w, cases);
  const checks = hand?.checks ? { ...merged, ...hand.checks } : merged;
  return {
    key,
    cases,
    context,
    topicSentences,
    positions,
    hints,
    checks,
    source: {
      context: hand?.context !== undefined ? 'hand' : 'generated',
      topicSentences: hand?.topicSentences ? 'hand' : 'generated',
      positions: hand?.positions ? 'hand' : 'generated',
      hints: hand?.hints ? 'hand' : 'generated',
      checks: hand?.checks ? 'hand' : 'generated',
    },
  };
}

// ---------------------------------------------------------------------------
// The review: every sentence for every pair, as one markdown document.
// ---------------------------------------------------------------------------

function describeClause(w: WorkshopBlock, c: WorkshopHints['order'][number]['when'][number]): string {
  if ('judgement' in c) {
    const label = w.scale?.labels[c.judgement] ?? `judgement ${c.judgement}`;
    const scope = c.themes && c.themes !== 'all' ? ` on the ${c.themes} ${w.themeWord.plural}` : '';
    const which = c.caseStudy && c.caseStudy !== 'both' ? ` for the ${c.caseStudy} ${w.unit.singular}` : '';
    const range = c.min !== undefined && c.max !== undefined ? `${c.min} to ${c.max}` : c.min !== undefined ? `at least ${c.min}` : c.max !== undefined ? `at most ${c.max}` : 'any number of';
    return `${range} cells judged "${label}"${scope}${which}`;
  }
  const theme = c.rankOf === 'anchor' ? `the anchor ${w.themeWord.singular}` : `${w.themeWord.singular} ${c.rankOf + 1}`;
  const range = c.min !== undefined && c.max !== undefined ? `between ${c.min + 1} and ${c.max + 1}` : c.min !== undefined ? `${c.min + 1} or lower` : c.max !== undefined ? `${c.max + 1} or higher` : 'anywhere';
  return `${theme} ranked ${range}`;
}

function renderPair(w: WorkshopBlock, p: PairContent, heading: string): string {
  const e = w.editions[p.cases[0]];
  const lines: string[] = [];
  lines.push(`### ${heading}`);
  lines.push('');
  lines.push(`**Context** (${p.source.context}): ${p.context}`);
  lines.push('');
  lines.push(`**Topic sentences** (${p.source.topicSentences})`);
  p.topicSentences.forEach((list, i) => {
    lines.push('');
    lines.push(`*${e.themes[i]?.name ?? `Theme ${i + 1}`}*`);
    list.forEach((t, j) => {
      lines.push(`${j + 1}. ${t.text}`);
      lines.push(`   You will need to show: ${t.need.replace(/^You will need to show /, '')}`);
    });
  });
  lines.push('');
  lines.push(`**Positions** (${p.source.positions})`);
  p.positions.forEach((pos, i) => {
    lines.push('');
    lines.push(`${i + 1}. **${pos.title}.** ${pos.detail}`);
    lines.push(`   Thesis stem: ${pos.build}`);
    lines.push(`   Reasons:`);
    for (const r of pos.reasons) lines.push(`   - (${e.themes[r.theme]?.name ?? r.theme}) ${r.text}`);
    lines.push(`   Counter-arguments:`);
    for (const r of pos.counters) lines.push(`   - (${e.themes[r.theme]?.name ?? r.theme}) ${r.text}`);
  });
  lines.push('');
  lines.push(`**Position hint** (${p.source.hints})`);
  if (p.hints.order.length === 0) lines.push(`- Always position ${p.hints.fallback + 1}.`);
  for (const h of p.hints.order) lines.push(`- Position ${h.position + 1} when ${h.when.map((c) => describeClause(w, c)).join(' and ')}.`);
  lines.push(`- Otherwise position ${p.hints.fallback + 1}.`);
  lines.push('');
  lines.push(`**Checks** (${p.source.checks}): claim words ${p.checks.claim.join(', ')}; link words ${p.checks.link.join(', ')}${p.checks.yearRange ? `; dates ${p.checks.yearRange[0]} to ${p.checks.yearRange[1]}` : ''}.`);
  return lines.join('\n');
}

/**
 * Every sentence a learner can meet for every reachable pair, marked
 * hand-written or generated. Where a pair is hand-written and could also be
 * generated, the generated version follows it, so the templates can be
 * checked against the real thing.
 */
export function renderPairReview(w: WorkshopBlock): string {
  const pairs = reachable(w).filter((s) => s.length === 2) as Array<[string, string]>;
  const out: string[] = [`# ${w.brand.title} ${w.brand.titleAccent}`, '', `Question: ${w.question}`, ''];
  if (pairs.length === 0) {
    out.push('This workshop has one case study at a time, so nothing is generated: each edition carries its own topic sentences and positions.');
    return out.join('\n');
  }
  out.push(`${pairs.length} pair${pairs.length === 1 ? '' : 's'} a learner can choose. Every sentence below is marked hand-written (from \`pairs\`) or generated (from the editions' compare lines by the templates in copy.ts).`);
  for (const cases of pairs) {
    const [a, b] = cases;
    const names = `${capitalise(w.caseStudies[a]?.name ?? a)} and ${w.caseStudies[b]?.name ?? b}`;
    const p = pairContent(w, cases);
    out.push('', `## ${names} (${workshopPairKey(a, b)})`, '');
    if (!p) {
      out.push(`Not writable: this pair is not in \`pairs\` and ${canGenerate(w, a, b) ? 'a field is missing' : 'a theme has no compare lines'}. The validator blocks it.`);
      continue;
    }
    out.push(renderPair(w, p, 'What the learner meets'));
    const anyHand = Object.values(p.source).includes('hand');
    if (anyHand && canGenerate(w, a, b)) {
      const gen: PairContent = {
        ...p,
        context: sentence(w.editions[a], 'pairContext', themeVars(w, a, b, 0)),
        topicSentences: Array.from({ length: w.editions[a].themes.length }, (_, i) => topicSentenceOptions(w, cases, i)),
        positions: generatePositions(w, cases),
        hints: generateHints(w),
        checks: mergeChecks(w, cases),
        source: { context: 'generated', topicSentences: 'generated', positions: 'generated', hints: 'generated', checks: 'generated' },
      };
      out.push('', renderPair(w, gen, 'What the generator would write for this pair'));
    }
  }
  return out.join('\n');
}

export { fillCopy };
